import React, { useState, useMemo } from 'react';
import { cleanMisconception } from '../../shared/utils/misconception';
import AskAlexPanel from './AskAlexPanel';
import ReportPanel from './ReportPanel';

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="white"
      strokeWidth="3"
      strokeLinecap="round"
    >
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="white"
      strokeWidth="3"
      strokeLinecap="round"
    >
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );
}

function splitExplanation(text) {
  const clean = (text || '').trim();
  if (!clean) return { first: '', rest: '' };
  const parts = clean.split('. ');
  if (parts.length <= 3) return { first: clean, rest: '' };
  const withPeriods = parts.map((s, i) => (i < parts.length - 1 ? s + '.' : s));
  return {
    first: withPeriods.slice(0, 3).join(' ').trim(),
    rest: withPeriods.slice(3).join(' ').trim(),
  };
}

function isUsefulMisconception(text) {
  if (!text) return false;
  const clean = String(text).trim();
  if (clean.length < 20) return false;
  if (!clean.includes('.')) return false;
  return true;
}

export default function QuestionResult({ question, result, onNext, isLast }) {
  const {
    correct,
    correct_answer,
    explanation,
    distractor_misconceptions,
    selected_answer,
  } = result;
  const [alexOpen, setAlexOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const options = question.options || [];
  const correctU = (correct_answer || '').toString().toUpperCase();
  const selectedU = (selected_answer || '').toString().toUpperCase();

  const { first: explanationFirst, rest: explanationRest } = useMemo(
    () => splitExplanation(explanation),
    [explanation]
  );

  const selectedMisconception = (() => {
    if (correct) return null;
    if (!distractor_misconceptions || typeof distractor_misconceptions !== 'object') return null;
    const raw = distractor_misconceptions[selected_answer];
    if (!raw) return null;
    const cleaned = cleanMisconception(raw);
    if (!isUsefulMisconception(cleaned)) return null;
    return cleaned;
  })();

  return (
    <div className="bg-bg-card rounded-r-xl border border-border-soft">
      <div className="px-sp-5 py-sp-5 lg:px-sp-6">
        {question.scenario && (
          <div className="bg-bg-surface rounded-r-md px-sp-3 py-sp-3 mb-sp-4">
            <p className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap">
              {question.scenario}
            </p>
          </div>
        )}
        <p className="text-[15px] font-semibold text-text-primary mb-sp-4">{question.stem}</p>

        <div className="flex flex-col gap-sp-2 mb-sp-4">
          {options.map((opt) => {
            const letter = opt.letter;
            const letterU = (letter || '').toString().toUpperCase();
            const isCorrect = letterU === correctU;
            const isWrong = letterU === selectedU && !correct;
            const isDimmed = !isCorrect && !isWrong;

            let stateCls = '';
            let letterCls = 'border-border-soft text-text-muted';
            let letterContent = letter;
            let textWeight = '';

            if (isCorrect) {
              stateCls = 'border-2 border-green bg-[#f0fdf4]';
              letterCls = 'bg-green border-green';
              letterContent = <CheckIcon />;
              textWeight = 'font-medium';
            } else if (isWrong) {
              stateCls = 'border-2 border-red bg-[#FEF2F2]';
              letterCls = 'bg-red border-red';
              letterContent = <CrossIcon />;
            } else if (isDimmed) {
              stateCls = 'border border-border-soft opacity-45 pointer-events-none';
            }

            return (
              <div
                key={letter}
                className={`flex items-center gap-sp-3 px-sp-3 py-sp-3 rounded-r-md ${stateCls}`}
              >
                <span
                  className={`w-[26px] h-[26px] rounded-full border-2 flex items-center justify-center text-xs font-semibold flex-shrink-0 ${letterCls}`}
                >
                  {letterContent}
                </span>
                <p className={`text-sm text-text-primary leading-relaxed ${textWeight}`}>
                  {opt.text}
                </p>
              </div>
            );
          })}
        </div>

        <div className="bg-bg-surface rounded-r-md px-sp-3 py-sp-3 mb-sp-3">
          <p
            className={`text-[11px] font-semibold uppercase tracking-wider mb-sp-2 ${
              correct ? 'text-green' : 'text-red'
            }`}
          >
            {correct ? 'Correct' : `Correct answer: ${correctU}`}
          </p>
          <p className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap">
            {explanationFirst}
          </p>

          {explanationRest && (
            <>
              {expanded && (
                <p className="mt-sp-3 text-sm text-text-primary leading-relaxed whitespace-pre-wrap">
                  {explanationRest}
                </p>
              )}
              <button
                onClick={() => setExpanded((v) => !v)}
                className="mt-sp-2 text-xs font-semibold text-accent hover:text-accent-hover transition-colors"
              >
                {expanded ? 'Hide' : 'Show why other options are wrong'}
              </button>
            </>
          )}
        </div>

        {selectedMisconception && (
          <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-r-md px-sp-3 py-sp-3 mb-sp-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider mb-sp-2 text-[#C2410C]">
              Common misconception
            </p>
            <p className="text-sm text-text-secondary leading-relaxed">
              {selectedMisconception}
            </p>
          </div>
        )}

        {alexOpen && (
          <AskAlexPanel
            questionId={question.id}
            wasCorrect={correct}
            onClose={() => setAlexOpen(false)}
          />
        )}

        {reportOpen && (
          <ReportPanel questionId={question.id} onClose={() => setReportOpen(false)} />
        )}

        <div className="flex items-center justify-between pt-sp-1">
          <div className="flex gap-sp-2">
            {!alexOpen && (
              <button
                onClick={() => setAlexOpen(true)}
                className="inline-flex items-center gap-sp-2 px-sp-3 py-sp-2 text-sm font-medium rounded-r-md bg-blue-lightest text-accent border border-[#BFDBFE] hover:bg-blue-light transition-colors"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                Ask Alex
              </button>
            )}
            {!reportOpen && (
              <button
                onClick={() => setReportOpen(true)}
                className="inline-flex items-center gap-sp-2 px-sp-3 py-sp-2 text-sm font-medium rounded-r-md text-text-secondary border border-border-soft hover:bg-bg-page transition-colors"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
                  <line x1="4" y1="22" x2="4" y2="15"></line>
                </svg>
                Report
              </button>
            )}
          </div>
          <button
            onClick={onNext}
            className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors active:scale-[0.98]"
          >
            {isLast ? 'View summary' : 'Next question'}
          </button>
        </div>
      </div>
    </div>
  );
}
